import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  LogOut,
  Inbox,
  CheckCircle2,
  Trash2,
  MoreVertical,
  ArrowLeft,
  Lock,
  Search,
  Send,
  FileText,
  Plus,
  X,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { company } from "@/data/company";
import {
  adminLogin,
  fetchInquiries,
  fetchStats,
  fetchLoginHistory,
  updateInquiry,
  deleteInquiry,
  sendNote,
  downloadInvoice,
} from "@/lib/apiClient";

const KEY = "netforge_admin_pw";
const fmtLKR = (n) => `LKR ${Number(n || 0).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`;
const HOURS_48_MS = 48 * 60 * 60 * 1000;
const needsFollowUp = (q) =>
  q.status === "new" && Date.now() - new Date(q.created_at).getTime() > HOURS_48_MS;

/* ---------------- Login ---------------- */
function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(username, pw);
      onLogin(pw);
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid username or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      className="min-h-screen flex items-center justify-center bg-[#0a0f1e] px-6"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-[#10182b] border border-white/10 rounded-sm p-8"
      >
        <div className="w-12 h-12 flex items-center justify-center bg-[#22d3ee] rounded-sm mb-6">
          <Lock size={20} className="text-[#0a0f1e]" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-white">
          {company.name} Admin
        </h1>
        <p className="text-sm text-slate-400 mt-2 mb-6">
          Enter your credentials to view inquiries.
        </p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username" className="text-slate-300">Username</Label>
            <Input
              id="username"
              data-testid="admin-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="netAdmin"
              className="bg-[#0a0f1e] border-white/10 text-white"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw" className="text-slate-300">Password</Label>
            <Input
              id="pw"
              type="password"
              data-testid="admin-password-input"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="••••••••"
              className="bg-[#0a0f1e] border-white/10 text-white"
            />
          </div>
        </div>
        <button
          type="submit"
          data-testid="admin-login-button"
          disabled={loading}
          className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-[#22d3ee] text-[#0a0f1e] font-bold px-6 py-3 rounded-sm hover:bg-[#67e8f9] transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : "Sign in"}
        </button>
        <Link
          to="/"
          className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-[#22d3ee] transition-colors"
        >
          <ArrowLeft size={15} /> Back to site
        </Link>
      </form>
    </div>
  );
}

/* ---------------- Note / Invoice dialog ---------------- */
function NoteDialog({ inquiry, pw, onClose, onSent }) {
  const [message, setMessage] = useState("");
  const [withInvoice, setWithInvoice] = useState(false);
  const [items, setItems] = useState([{ service: "", cost: "" }]);
  const [sending, setSending] = useState(false);

  const total = items.reduce((s, i) => s + (parseFloat(i.cost) || 0), 0);

  const updateItem = (idx, k, v) =>
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((arr) => [...arr, { service: "", cost: "" }]);
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!message.trim()) {
      toast.error("Please write a message.");
      return;
    }
    let invoice_items = null;
    if (withInvoice) {
      invoice_items = items
        .filter((i) => i.service.trim() && i.cost !== "")
        .map((i) => ({ service: i.service.trim(), cost: parseFloat(i.cost) }));
      if (invoice_items.length === 0) {
        toast.error("Add at least one invoice line with a service and cost.");
        return;
      }
    }
    setSending(true);
    try {
      const res = await sendNote(inquiry.id, { message, invoice_items }, pw);
      if (res.data.email_sent) {
        toast.success("Note emailed to the customer.");
      } else {
        toast.warning("Note saved, but the email could not be sent.");
      }
      onSent();
      onClose();
    } catch {
      toast.error("Failed to send note.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="note-dialog"
        className="bg-[#10182b] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">
            Send note to {inquiry.name}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Email a message to the customer, optionally attaching a PDF invoice.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-xs text-slate-400">
            Ref: <span className="text-[#22d3ee] font-bold">{inquiry.ref || "—"}</span>
            {"  •  "}
            {inquiry.email}
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Message</Label>
            <Textarea
              data-testid="note-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your message to the customer..."
              rows={4}
              className="bg-[#0a0f1e] border-white/10 text-white resize-none"
            />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Checkbox
              id="with-invoice"
              data-testid="note-invoice-toggle"
              checked={withInvoice}
              onCheckedChange={(v) => setWithInvoice(!!v)}
              className="border-white/30 data-[state=checked]:bg-[#22d3ee] data-[state=checked]:border-[#22d3ee]"
            />
            <Label htmlFor="with-invoice" className="text-slate-300 cursor-pointer">
              Attach an invoice (PDF, LKR)
            </Label>
          </div>

          {withInvoice && (
            <div className="border border-white/10 rounded-sm p-4 space-y-3 bg-[#0a0f1e]">
              {items.map((it, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input
                    data-testid={`invoice-service-${idx}`}
                    value={it.service}
                    onChange={(e) => updateItem(idx, "service", e.target.value)}
                    placeholder="Service description"
                    className="bg-[#10182b] border-white/10 text-white flex-1"
                  />
                  <Input
                    data-testid={`invoice-cost-${idx}`}
                    type="number"
                    value={it.cost}
                    onChange={(e) => updateItem(idx, "cost", e.target.value)}
                    placeholder="Cost"
                    className="bg-[#10182b] border-white/10 text-white w-32"
                  />
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      className="text-slate-500 hover:text-red-400 p-1"
                      aria-label="Remove line"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                data-testid="invoice-add-line"
                onClick={addItem}
                className="inline-flex items-center gap-1 text-sm text-[#22d3ee] hover:text-[#67e8f9]"
              >
                <Plus size={14} /> Add line
              </button>
              <div className="flex justify-between items-center border-t border-white/10 pt-3 text-sm">
                <span className="text-slate-400">Total</span>
                <span data-testid="invoice-total" className="font-heading font-bold text-[#22d3ee]">
                  {fmtLKR(total)}
                </span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm text-slate-300 border border-white/10 rounded-sm hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            data-testid="note-send-button"
            onClick={submit}
            disabled={sending}
            className="inline-flex items-center gap-2 bg-[#22d3ee] text-[#0a0f1e] font-bold px-5 py-2.5 rounded-sm hover:bg-[#67e8f9] transition-colors disabled:opacity-60"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Send
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Dashboard ---------------- */
export default function Admin() {
  const [pw, setPw] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, handled: 0 });
  const [history, setHistory] = useState([]);
  const [query, setQuery] = useState("");
  const [noteFor, setNoteFor] = useState(null);

  const loadData = useCallback(async (password) => {
    setLoading(true);
    try {
      const [inq, st, hist] = await Promise.all([
        fetchInquiries(password),
        fetchStats(password),
        fetchLoginHistory(password),
      ]);
      setInquiries(inq.data);
      setStats(st.data);
      setHistory(hist.data.history || []);
    } catch {
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(KEY);
    if (saved) {
      setPw(saved);
      setAuthed(true);
      loadData(saved);
    }
  }, [loadData]);

  const handleLogin = (password) => {
    localStorage.setItem(KEY, password);
    setPw(password);
    setAuthed(true);
    loadData(password);
  };

  const logout = () => {
    localStorage.removeItem(KEY);
    setAuthed(false);
    setPw("");
    setInquiries([]);
  };

  const setStatus = async (id, status) => {
    try {
      await updateInquiry(id, status, pw);
      toast.success("Status updated.");
      loadData(pw);
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (id) => {
    try {
      await deleteInquiry(id, pw);
      toast.success("Inquiry deleted.");
      loadData(pw);
    } catch {
      toast.error("Delete failed.");
    }
  };

  const getInvoice = async (q) => {
    try {
      const res = await downloadInvoice(q.id, pw);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${q.invoice?.invoice_no || q.ref}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("No invoice available.");
    }
  };

  if (!authed) return <LoginScreen onLogin={handleLogin} />;

  const followUpCount = inquiries.filter(needsFollowUp).length;
  const statCards = [
    { label: "Total inquiries", value: stats.total, icon: Inbox },
    { label: "New", value: stats.new, icon: Inbox },
    { label: "Handled", value: stats.handled, icon: CheckCircle2 },
    { label: "Needs follow-up", value: followUpCount, icon: AlertTriangle, alert: true },
  ];

  const q = query.trim().toLowerCase();
  const filtered = q
    ? inquiries.filter(
        (i) =>
          (i.ref || "").toLowerCase().includes(q) ||
          (i.name || "").toLowerCase().includes(q) ||
          (i.email || "").toLowerCase().includes(q)
      )
    : inquiries;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0a0f1e]">
      <header className="border-b border-white/10 bg-[#0d1526]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-header.png" alt={`${company.name} logo`} className="h-9 w-auto object-contain" />
            <span className="text-slate-500 font-medium font-heading">/ Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-slate-400 hover:text-[#22d3ee] transition-colors">
              View site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm text-slate-300 border border-white/10 px-4 py-2 rounded-sm hover:bg-white/5 transition-colors"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
          {statCards.map((c) => {
            const active = c.alert && c.value > 0;
            return (
              <div key={c.label} data-testid={`stat-card-${c.label}`} className="bg-[#10182b] p-6">
                <div className="flex items-center gap-3 text-slate-400 mb-3">
                  <c.icon size={18} className={active ? "text-amber-400" : "text-[#22d3ee]"} />
                  <span className="text-xs uppercase tracking-wider">{c.label}</span>
                </div>
                <div className={`font-heading text-4xl font-black ${active ? "text-amber-400" : "text-white"}`}>
                  {c.value}
                </div>
              </div>
            );
          })}
        </div>

        <Tabs defaultValue="inquiries">
          <TabsList className="bg-[#10182b] border border-white/10">
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Login history</TabsTrigger>
          </TabsList>

          {/* Inquiries */}
          <TabsContent value="inquiries" className="mt-6">
            <div className="relative mb-5 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                data-testid="inquiry-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by reference no., name or email..."
                className="pl-9 bg-[#10182b] border-white/10 text-white"
              />
            </div>

            <div className="border border-white/10 rounded-sm overflow-hidden bg-[#10182b]">
              {loading ? (
                <div className="p-16 flex items-center justify-center text-slate-400">
                  <Loader2 className="animate-spin mr-2" /> Loading...
                </div>
              ) : filtered.length === 0 ? (
                <div data-testid="admin-empty-state" className="p-16 text-center text-slate-400">
                  {inquiries.length === 0 ? "No inquiries yet." : "No matching inquiries."}
                </div>
              ) : (
                <Table data-testid="inquiries-table">
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-slate-400">Ref</TableHead>
                      <TableHead className="text-slate-400">Name</TableHead>
                      <TableHead className="text-slate-400">Email</TableHead>
                      <TableHead className="text-slate-400 hidden md:table-cell">Service</TableHead>
                      <TableHead className="text-slate-400 hidden lg:table-cell">Message</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((q2) => {
                      const overdue = needsFollowUp(q2);
                      return (
                      <TableRow
                        key={q2.id}
                        data-testid={`inquiry-row-${q2.id}`}
                        className={`border-white/10 hover:bg-white/5 ${overdue ? "bg-amber-400/5" : ""}`}
                      >
                        <TableCell className={`font-mono font-bold whitespace-nowrap ${overdue ? "border-l-2 border-amber-400 pl-3" : ""} text-[#22d3ee]`}>
                          {q2.ref || "—"}
                        </TableCell>
                        <TableCell className="text-white font-medium">
                          {q2.name}
                          {q2.company && <div className="text-xs text-slate-500">{q2.company}</div>}
                        </TableCell>
                        <TableCell className="text-slate-300">{q2.email}</TableCell>
                        <TableCell className="text-slate-300 hidden md:table-cell">
                          {q2.service || "—"}
                        </TableCell>
                        <TableCell className="text-slate-400 hidden lg:table-cell max-w-xs truncate">
                          {q2.message}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge
                              className={
                                q2.status === "new"
                                  ? "bg-[#22d3ee] text-[#0a0f1e] hover:bg-[#22d3ee]"
                                  : "bg-slate-700 text-slate-200 hover:bg-slate-700"
                              }
                            >
                              {q2.status}
                            </Badge>
                            {overdue && (
                              <Badge
                                data-testid={`follow-up-badge-${q2.id}`}
                                className="bg-amber-400/15 text-amber-400 border border-amber-400/30 hover:bg-amber-400/15 gap-1"
                              >
                                <AlertTriangle size={11} /> Follow up
                              </Badge>
                            )}
                            {q2.invoice && (
                              <FileText size={14} className="text-slate-500" title="Invoice generated" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                data-testid={`inquiry-actions-${q2.id}`}
                                className="text-slate-400 hover:text-white p-2"
                              >
                                <MoreVertical size={18} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                data-testid={`send-note-${q2.id}`}
                                onClick={() => setNoteFor(q2)}
                              >
                                <Send size={14} className="mr-2" /> Send note / invoice
                              </DropdownMenuItem>
                              {q2.invoice && (
                                <DropdownMenuItem
                                  data-testid={`download-invoice-${q2.id}`}
                                  onClick={() => getInvoice(q2)}
                                >
                                  <FileText size={14} className="mr-2" /> Download invoice
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setStatus(q2.id, "new")}>
                                Mark as new
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setStatus(q2.id, "handled")}>
                                Mark as handled
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-500 focus:text-red-500"
                                onClick={() => remove(q2.id)}
                              >
                                <Trash2 size={14} className="mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* Login history */}
          <TabsContent value="history" className="mt-6">
            <div className="border border-white/10 rounded-sm overflow-hidden bg-[#10182b]">
              {history.length === 0 ? (
                <div className="p-16 text-center text-slate-400">No login attempts recorded.</div>
              ) : (
                <Table data-testid="login-history-table">
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-slate-400">Time (UTC)</TableHead>
                      <TableHead className="text-slate-400">Username</TableHead>
                      <TableHead className="text-slate-400">Result</TableHead>
                      <TableHead className="text-slate-400 hidden md:table-cell">IP</TableHead>
                      <TableHead className="text-slate-400 hidden lg:table-cell">Device</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((h, i) => (
                      <TableRow key={i} className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-slate-300 whitespace-nowrap">
                          {new Date(h.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-white">{h.username}</TableCell>
                        <TableCell>
                          {h.success ? (
                            <span className="inline-flex items-center gap-1.5 text-emerald-400 text-sm">
                              <ShieldCheck size={15} /> Success
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-red-400 text-sm">
                              <ShieldAlert size={15} /> Failed
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-400 hidden md:table-cell">{h.ip}</TableCell>
                        <TableCell className="text-slate-500 hidden lg:table-cell max-w-xs truncate text-xs">
                          {h.user_agent}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {noteFor && (
        <NoteDialog
          inquiry={noteFor}
          pw={pw}
          onClose={() => setNoteFor(null)}
          onSent={() => loadData(pw)}
        />
      )}
    </div>
  );
}
